import React, { createContext, useState, useContext, useEffect } from 'react';
import API from '../api';
import socket from '../socket';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const usuarioGuardado = localStorage.getItem('usuario');
    if (usuarioGuardado) {
      setUsuario(JSON.parse(usuarioGuardado));
    }
    setCargando(false);
  }, []);

  const login = async (usuarioInput, clave) => {
    const response = await API.post('/auth/login', { usuario: usuarioInput, clave });
    if (response.data.status === 'success') {
      setUsuario(response.data.usuario);
      localStorage.setItem('usuario', JSON.stringify(response.data.usuario));
      // Re-autenticar el socket: al cargar la app el socket se conectó ANÓNIMO
      // (la pantalla de Login aún no tiene token). Forzamos una reconexión para
      // que el handshake use el token de sesión recién emitido y el backend
      // autorice el join_room a la sala de asesores de la empresa.
      try {
        if (socket.connected) socket.disconnect();
        socket.connect();
      } catch (e) {
        /* best-effort */
      }
      return true;
    }
    return false;
  };

  const logout = async () => {
    try {
      const u = JSON.parse(localStorage.getItem('usuario') || 'null');
      if (u?.token) {
        await API.post('/auth/logout', {}, { headers: { 'x-session-token': u.token } });
      }
    } catch (e) {
      /* best-effort */
    }
    setUsuario(null);
    localStorage.removeItem('usuario');
    // Cerrar la conexión para no seguir recibiendo eventos de la empresa anterior.
    try {
      socket.disconnect();
    } catch (e) {
      /* best-effort */
    }
  };

  const refrescarPermisos = async () => {
    try {
      const u = JSON.parse(localStorage.getItem('usuario') || 'null');
      if (!u?.token) return;
      const res = await API.get('/auth/me');
      if (res.data.status === 'success') {
        const data = res.data.data;
        if (data) {
          const merged = { ...u, ...data, token: u.token };
          setUsuario(merged);
          localStorage.setItem('usuario', JSON.stringify(merged));
        }
      }
    } catch (e) {
      /* best-effort */
    }
  };

  const isAdmin = () => usuario?.rol === 'admin' || usuario?.es_super_admin === true;
  const isSuperAdmin = () => usuario?.es_super_admin === true;
  const isSupervisor = () => usuario?.rol === 'supervisor';
  const isAsesor = () => usuario?.rol === 'asesor';
  const isDelivery = () => usuario?.rol === 'delivery';
  const isArmador = () => usuario?.rol === 'armador';
  const isReadOnly = () => false;

  const planTipo = usuario?.plan_tipo || 'interno';
  const isPlanExterno = () => usuario?.plan_tipo === 'externo';

  const tipoEmpresa = usuario?.tipo_empresa || 'general';
  const isTipoEmpresa = (...tipos) => tipos.includes(tipoEmpresa);

  const permisosArr = usuario?.permisos;
  const tienePermiso = (codigo) => {
    if (!usuario) return false;
    if (usuario.es_super_admin === true) return true;
    if (isAdmin()) return true;
    if (!permisosArr) return false;
    if (permisosArr.length === 0) return false;
    if (Array.isArray(codigo)) return codigo.some((c) => permisosArr.includes(c));
    return permisosArr.includes(codigo);
  };

  const tieneVisibilidad = (codigo) => tienePermiso(codigo);

  const getRolLabel = () => {
    if (usuario?.rol_nombre) return usuario.rol_nombre;
    const labels = {
      admin: 'Admin',
      supervisor: 'Supervisor',
      asesor: 'Asesor',
      delivery: 'Delivery',
      armador: 'Armador',
    };
    return labels[usuario?.rol] || 'Asesor';
  };

  const getSedeInfo = () => {
    if (!usuario?.codigo_sede) return null;
    return {
      codigo: usuario.codigo_sede,
      nombre: usuario.sede_nombre || usuario.codigo_sede,
    };
  };

  return (
    <AuthContext.Provider
      value={{
        usuario,
        cargando,
        login,
        logout,
        refrescarPermisos,
        isAdmin,
        isSuperAdmin,
        isSupervisor,
        isAsesor,
        isDelivery,
        isArmador,
        isReadOnly,
        planTipo,
        isPlanExterno,
        tipoEmpresa,
        isTipoEmpresa,
        getRolLabel,
        getSedeInfo,
        tienePermiso,
        tp: tienePermiso,
        permisos: permisosArr,
        tieneVisibilidad,
        v: tieneVisibilidad,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
